import React, { useState } from 'react'
import styled from 'styled-components'
import {
  useProfile,
  useErrors,
  DefaultFormErrors,
  useLocalLabels,
  useUpdateProfile,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'
import { useForm, Controller } from 'react-hook-form'
import {
  Button,
  FormError,
  Input,
  InputLabel as Label,
  Select,
} from '../../../Common'
import { ProfilePreview } from './Components'
import { useProfileDispatch } from '../../profile.context'
import { ProfileLabels } from '../../profile.props'

const dayOptions = new Array(31).fill('').map((_, index) => {
  const value = index + 1
  return {
    value,
    label: value,
  }
})

const currentYear = new Date().getFullYear()
const yearOptions = new Array(100).fill('').map((_, index) => {
  const value = currentYear - index
  return {
    value,
    label: value,
  }
})

export const AccountSettings = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const { accountSettings, personalInfo } = useLocalLabels<ProfileLabels>()
  const {
    errorMessages,
    globalValidations,
    password,
    cancel,
    save,
    placeholders,
  } = useGlobalLabels()
  const { handleSubmit, errors, control, setError } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const openForm = () => setIsEditing(true)
  const closeForm = () => setIsEditing(false)

  const { data: userData } = useProfile()

  const [mutate, { isLoading, error }] = useUpdateProfile({
    onSuccess: closeForm,
  })

  const apiErrors = useErrors<DefaultFormErrors>(error, {
    default: errorMessages?.somethingWentWrong,
  })

  // TODO: make typezation
  const onSubmit = async (data: any) => {
    if (!userData) return

    const today = new Date()
    const inputDate = new Date(
      `${data?.yearOfBirth}/${data.monthOfBirth}/${data.dayOfBirth}`
    )
    if (today <= inputDate) {
      if (inputDate.getMonth() > today.getMonth()) {
        return setError('monthOfBirth', {
          type: 'validate',
          message: 'Моля изберете месец преди текущия',
        })
      } else if (inputDate.getDate() >= today.getDate()) {
        return setError('dayOfBirth', {
          type: 'validate',
          message: 'Моля изберете дата преди текущата',
        })
      }
    }
    if (!hasErrors) {
      mutate({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: +data.gender as any,
        dayOfBirth: +data.dayOfBirth,
        monthOfBirth: +data.monthOfBirth,
        yearOfBirth: +data.yearOfBirth,
        _id: (userData as any)._id,
        status: (userData as any).status,
      })
    }
  }

  const updateTab = useProfileDispatch()

  const goToPasswordUpdate = () => {
    updateTab({
      type: 'change',
      payload: 'update-password',
    })
  }

  const hasErrors = !!Object.keys(errors).length

  const buttonDisabled = isLoading || hasErrors

  if (!isEditing) {
    return <ProfilePreview openForm={openForm} />
  }

  return (
    <div>
      <Title>{personalInfo?.title}</Title>
      <RequiredFields>{personalInfo?.requiredFieldsTitle}</RequiredFields>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <InputsWrapper>
          <Label htmlFor="edit-first-name">
            {personalInfo?.requiredFieldsLabels?.firstName}
            {personalInfo?.requiredFieldsLabels?.requiredSign}
            <Controller
              control={control}
              name="firstName"
              defaultValue={userData?.firstName}
              rules={{
                required: globalValidations?.fieldIsRequired,
              }}
              as={<Input id="edit-first-name" />}
            />
            <FormError errors={errors} fieldName="firstName" />
          </Label>

          <Label htmlFor="edit-last-name">
            {personalInfo?.requiredFieldsLabels?.lastName}
            {personalInfo?.requiredFieldsLabels?.requiredSign}
            <Controller
              control={control}
              name="lastName"
              defaultValue={userData?.lastName}
              rules={{
                required: globalValidations?.fieldIsRequired,
              }}
              as={<Input id="edit-last-name" />}
            />
            <FormError errors={errors} fieldName="lastName" />
          </Label>

          <Label htmlFor="">
            {personalInfo?.otherLabels?.dob}
            <DOBContainer>
              <div className="col-4">
                <Controller
                  control={control}
                  name="dayOfBirth"
                  defaultValue={userData?.dayOfBirth}
                  render={({ onChange, value }) => (
                    <Select
                      id="edit-day-of-birth"
                      value={value}
                      placeholder={placeholders?.day}
                      onChange={onChange}
                      options={dayOptions}
                    />
                  )}
                />
                <FormError errors={errors} fieldName="dayOfBirth" />
              </div>
              <div className="col-4">
                <Controller
                  control={control}
                  name="monthOfBirth"
                  defaultValue={userData?.monthOfBirth}
                  render={({ onChange, value }) => (
                    <Select
                      id="edit-month-of-birth"
                      value={value}
                      placeholder={placeholders?.month}
                      onChange={onChange}
                      options={accountSettings?.monthOptions}
                    />
                  )}
                />
                <FormError errors={errors} fieldName="monthOfBirth" />
              </div>
              <div className="col-4">
                <Controller
                  control={control}
                  name="yearOfBirth"
                  defaultValue={userData?.yearOfBirth}
                  render={({ onChange, value }) => (
                    <Select
                      id="edit-year-of-birth"
                      value={value}
                      placeholder={placeholders?.year}
                      onChange={onChange}
                      options={yearOptions}
                    />
                  )}
                />
                <FormError errors={errors} fieldName="yearOfBirth" />
              </div>
            </DOBContainer>
          </Label>

          <Label htmlFor="edit-email">
            {personalInfo?.requiredFieldsLabels?.email}
            {personalInfo?.requiredFieldsLabels?.requiredSign}
            <Controller
              control={control}
              name="email"
              defaultValue={userData?.email}
              rules={{
                required: globalValidations?.fieldIsRequired,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: globalValidations?.invalidEmail,
                },
              }}
              as={<Input id="edit-email" type="email" />}
            />
            <FormError errors={errors} fieldName="email" />
          </Label>

          <Label htmlFor="edit-gender">
            {personalInfo?.otherLabels?.gender}
            <Controller
              control={control}
              name="gender"
              defaultValue={userData?.gender}
              render={({ onChange, value }) => (
                <Select
                  id="edit-gender"
                  value={value}
                  onChange={onChange}
                  options={[
                    {
                      value: 0,
                      label: accountSettings?.unspecified,
                    },
                    {
                      value: 1,
                      label: accountSettings?.male,
                    },
                    {
                      value: 2,
                      label: accountSettings?.female,
                    },
                  ]}
                />
              )}
            />
            <FormError errors={errors} fieldName="email" />
          </Label>
          <ChangePassword onClick={goToPasswordUpdate}>
            {password?.changePassword}
          </ChangePassword>
        </InputsWrapper>

        <SubmitWrapper>
          <Button
            variant="green"
            bordered
            onClick={closeForm}
            borderRadius="small"
          >
            {cancel}
          </Button>

          <Button
            type="submit"
            disabled={buttonDisabled}
            className="ml-4"
            variant={buttonDisabled ? 'disabled' : 'green'}
            borderRadius="small"
          >
            {save}
          </Button>
        </SubmitWrapper>
        <FormError errors={apiErrors} fieldName="default" />
      </form>
    </div>
  )
}

const InputsWrapper = styled.div.attrs({})`
  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: 52.5%;
  }
`

const DOBContainer = styled.div.attrs({
  className: 'row g-3 g-lg-4',
})``

const Title = styled.h6.attrs({
  className: 'mb-3',
})``

const RequiredFields = styled.p.attrs({
  className: 'mb-4',
})`
  color: ${({ theme }) => theme.palette.hint};
`

const SubmitWrapper = styled.div.attrs({
  className: 'mt-5 pt-5',
})`
  border-top: 1px solid ${({ theme }) => theme.palette.lightgrey};
  display: flex;
  justify-content: flex-end;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    & button {
      width: 21% !important;
    }
  }
`

const ChangePassword = styled.span.attrs({
  className: 'my-3 d-inline-block',
})`
  color: ${({ theme }) => theme.palette.hint};
  font-size: 1.2rem;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`
